import { useCallback, useEffect, useState } from 'react'

import { logger } from '@/utils'

const log = logger.child({
  file: 'src/hooks/useLocalStorage.tsx',
})

export const useLocalStorage = <T,>(key: string, initialValue?: T) => {
  // Initialize state with the value from localStorage or the initial value
  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    const item = localStorage.getItem(key)
    try {
      if (item) {
        return JSON.parse(item) || item
      }
    } catch (e) {
      return item
      log.debug(e)
    }
    return initialValue
  })

  // Update the state and localStorage whenever the value changes
  const setValue = useCallback(
    (value?: T) => {
      try {
        // Save to state
        setStoredValue(value)

        // Save to localStorage
        if (value) {
          localStorage.setItem(key, JSON.stringify(value))
        } else {
          localStorage.removeItem(key)
        }

        // Trigger a custom event to notify other parts of the app
        window.dispatchEvent(new Event(`storage-update`))
      } catch (error) {
        console.error('Error setting local storage', error)
      }
    },
    [key],
  )

  // Listen for storage events and update the state if the relevant key changes
  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    const handleStorageChange = (event: StorageEvent | Event) => {
      // `StorageEvent` only triggers on other tabs/windows, so we also trigger `storage-update` manually
      if (event instanceof StorageEvent) {
        if (event.key === key && event.newValue !== null) {
          try {
            setStoredValue(JSON.parse(event.newValue))
          } catch (e) {
            setStoredValue(JSON.parse(JSON.stringify(event.newValue)))
            log.debug(e)
          }
        }
      } else if (event.type === 'storage-update') {
        // Update from the custom event
        const item = localStorage.getItem(key)
        if (item) {
          try {
            const newItem = JSON.parse(item) || item
            setStoredValue(newItem)
          } catch (e) {
            setStoredValue(JSON.parse(JSON.stringify(item)))
            log.debug(e)
          }
        }
      }
    }

    // Listen for both native and custom events
    window.addEventListener('storage', handleStorageChange, { signal })
    window.addEventListener('storage-update', handleStorageChange, { signal })

    // Cleanup
    return () => {
      controller.abort()
    }
  }, [key])

  return [storedValue, setValue] as const
}
